import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Button, Row, Col, Card, Modal } from 'react-bootstrap';
import './Calendar.css'; // Import the Calendar CSS
import logoImage from '../images/offplanner-logo.png'; // Import the logo image
import MobileMenuBar from './MobileMenuBar'; // Import the new mobile menu bar

/**
 * Calendar component for the Off Planner application
 * Displays a monthly calendar with shift information
 */
const Calendar = ({ currentUser }) => {
  // State variables
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [companies, setCompanies] = useState([]);
  const [companiesData, setCompaniesData] = useState({}); // Store full company data including country
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedPattern, setSelectedPattern] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const [shifts, setShifts] = useState([]);
  // State to store the user's initially loaded preferred settings
  const [userInitialPreferredCompany, setUserInitialPreferredCompany] = useState('');
  const [userInitialPreferredPattern, setUserInitialPreferredPattern] = useState('');
  const [userInitialPreferredShift, setUserInitialPreferredShift] = useState('');
  const [patterns, setPatterns] = useState([]);
  const [companyLogo, setCompanyLogo] = useState(''); // State for company logo
  const [friends, setFriends] = useState([]);
  // Store all shift patterns for all friends (and user)
  const [allFriendsShifts, setAllFriendsShifts] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);
  // State for user's booked holidays
  const [userHolidays, setUserHolidays] = useState([]);
  
  // Touch event state for swipe detection
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Date selection modal states
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDateShifts, setSelectedDateShifts] = useState([]);
  
  // Day names for the calendar header
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  
  // Month names for display
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  // Default Mon-Fri 9-5 pattern
  const defaultPattern = {
    id: 'default-pattern',
    company: 'Default',
    patternName: 'Mon-Fri 9-5',
    shift: 'Day',
    startDate: new Date().toISOString().split('T')[0], // Today's date
    shiftPattern: 7, // 7-day pattern
    // The pattern array is not actually used for the default pattern
    // We directly check the day of week in getShiftType function
    pattern: [
      'D', 'D', 'D', 'D', 'D', 'X', 'X' // Monday to Friday work, weekend off
    ]
  };
  
  // Bank holidays for UK and Ireland - wrapped in useMemo to prevent recreation on every render
  const bankHolidaysData = useMemo(() => ({
    UK: {
      2024: [
        '2024-01-01',  // New Year's Day
        '2024-03-29',  // Good Friday
        '2024-04-01',  // Easter Monday
        '2024-05-06',  // Early May Bank Holiday
        '2024-05-27',  // Spring Bank Holiday
        '2024-08-26',  // Summer Bank Holiday
        '2024-12-25',  // Christmas Day
        '2024-12-26'   // Boxing Day
      ],
      2025: [
        '2025-01-01',  // New Year's Day
        '2025-04-18',  // Good Friday
        '2025-04-21',  // Easter Monday
        '2025-05-05',  // Early May Bank Holiday
        '2025-05-26',  // Spring Bank Holiday
        '2025-08-25',  // Summer Bank Holiday
        '2025-12-25',  // Christmas Day
        '2025-12-26'   // Boxing Day
      ]
    },
    Ireland: {
      2024: [
        '2024-01-01',  // New Year's Day
        '2024-03-17',  // St. Patrick's Day
        '2024-03-29',  // Good Friday
        '2024-04-01',  // Easter Monday
        '2024-05-06',  // May Bank Holiday
        '2024-06-03',  // June Bank Holiday
        '2024-08-05',  // August Bank Holiday
        '2024-10-28',  // October Bank Holiday
        '2024-12-25',  // Christmas Day
        '2024-12-26'   // St. Stephen's Day
      ],
      2025: [
        '2025-01-01',  // New Year's Day
        '2025-03-17',  // St. Patrick's Day
        '2025-04-18',  // Good Friday
        '2025-04-21',  // Easter Monday
        '2025-05-05',  // May Bank Holiday
        '2025-06-02',  // June Bank Holiday
        '2025-08-04',  // August Bank Holiday
        '2025-10-27',  // October Bank Holiday
        '2025-12-25',  // Christmas Day
        '2025-12-26'   // St. Stephen's Day
      ]
    }
  }), []); // Empty dependency array means this will only be created once
  
  // Helper function to check if a date is a bank holiday
  const isBankHoliday = useCallback((day) => {
    // Ensure we have company data and day is a valid object
    if (!companiesData || !selectedCompany || !companiesData[selectedCompany] || !day || typeof day !== 'object') {
      return false;
    }
    
    // Get country from company data or default to UK
    const country = companiesData[selectedCompany]?.country || 'UK';
    
    // Ensure day has year, month, and day properties
    if (!day.hasOwnProperty('year') || !day.hasOwnProperty('month') || !day.hasOwnProperty('day')) {
      return false;
    }
    
    const year = day.year;
    const dateStr = `${year}-${String(day.month + 1).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
    
    // Check if we have bank holiday data for this country and year
    const hasCountryData = Boolean(bankHolidaysData[country]);
    const hasYearData = hasCountryData && Boolean(bankHolidaysData[country][year]);
    const isArray = hasYearData && Array.isArray(bankHolidaysData[country][year]);
    const isIncluded = isArray && bankHolidaysData[country][year].includes(dateStr);
    
    return isIncluded;
  }, [bankHolidaysData, companiesData, selectedCompany]);
  
  
  // Helper function to check if a date is within a booked holiday period
  const isHolidayBooked = useCallback((day) => {
    if (!userHolidays || userHolidays.length === 0) {
      return false;
    }
    
    // Create a date object for the current day and normalize to midnight
    const date = new Date(day.year, day.month, day.day).setHours(0, 0, 0, 0);
    
    // Check if the date falls within any booked holiday period
    const isBooked = userHolidays.some(holiday => {
      // Ensure we're working with Date objects and normalize to midnight
      const startDate = new Date(holiday.startDate).setHours(0, 0, 0, 0);
      const endDate = new Date(holiday.endDate).setHours(0, 0, 0, 0);
      
      // Check if the current date is within the holiday range
      const isWithinRange = date >= startDate && date <= endDate;
      
      // For debugging
      if (isWithinRange) {
        console.log('Holiday match found:', {
          day: `${day.year}-${day.month + 1}-${day.day}`,
          holiday: {
            start: new Date(startDate).toISOString().split('T')[0],
            end: new Date(endDate).toISOString().split('T')[0]
          }
        });
      }
      
      return isWithinRange;
    });
    
    return isBooked;
  }, [userHolidays]);

  // Function to fetch user's booked holidays
  const fetchUserBookedHolidays = useCallback(async () => {
    // setLoadingUserHolidays(true); // Optional: set loading state
    if (!currentUser) {
      setUserHolidays([]);
      // setLoadingUserHolidays(false); // Optional: set loading state
      return;
    }
    try {
      console.log('Refreshing user holidays for Calendar...'); // Add a log for when this is called
      const holidaysCollectionRef = collection(db, 'holidays');
      const q = query(holidaysCollectionRef, where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const holidays = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Ensure dates are JS Date objects
        startDate: doc.data().startDate.toDate ? doc.data().startDate.toDate() : new Date(doc.data().startDate),
        endDate: doc.data().endDate.toDate ? doc.data().endDate.toDate() : new Date(doc.data().endDate),
      }));
      setUserHolidays(holidays);
    } catch (error) {
      console.error('Error fetching user holidays for Calendar:', error);
      // setErrorUserHolidays('Failed to load your booked holidays.'); // Optional: set error state
    }
    // setLoadingUserHolidays(false); // Optional: set loading state
  }, [currentUser]); // Removed db from dependencies as it's stable

  // Fetch user's booked holidays when currentUser changes or on explicit refresh
  useEffect(() => {
    fetchUserBookedHolidays();
  }, [currentUser, fetchUserBookedHolidays]); // Re-fetch if currentUser or the function itself changes (e.g. db instance)

  // Fetch companies, patterns, and user preferences (if logged in) on mount or when currentUser changes
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        console.log('Initializing Calendar component');
        
        // Always set default company and pattern first
        // This ensures we have a fallback even if Firebase access fails
        const uniqueCompanies = ['Default']; 
        const companyPatterns = {
          'Default': ['Mon-Fri 9-5']
        };
        
        // For non-logged-in users, we still want to access real companies from Firebase
        // We'll attempt to fetch them regardless of login status
        if (!currentUser) {
          console.log('No user logged in, but still attempting to fetch real companies');
        }
        
        // Try to fetch from Firebase if possible
        try {
          // Fetch companies from the companies collection
          const companiesRef = collection(db, 'companies');
          const companiesSnapshot = await getDocs(companiesRef);
          
          // Create a map of company names to their data (logo, country, etc.)
          const companyData = {};
          companiesSnapshot.forEach((doc) => {
            const data = doc.data();
            companyData[data.name] = {
              logoUrl: data.logoUrl || '',
              country: data.country || 'UK' // Default to UK if no country is specified
            };
          });
          
          // Store the full company data
          setCompaniesData(companyData);
          
          console.log('Fetched company data:', Object.keys(companyData).length);
          
          // Fetch shift patterns
          const shiftsRef = collection(db, 'shiftPatterns');
          const querySnapshot = await getDocs(shiftsRef);
          
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const companyName = data.company;
            const patternName = data.patternName;
            
            if (!uniqueCompanies.includes(companyName)) {
              uniqueCompanies.push(companyName);
            }
            
            if (!companyPatterns[companyName]) {
              companyPatterns[companyName] = [];
            }
            
            if (!companyPatterns[companyName].includes(patternName)) {
              companyPatterns[companyName].push(patternName);
            }
          });
          
          // If there are actual companies from Firebase, keep them as options
          if (uniqueCompanies.length > 1) {
            console.log('Firebase companies available:', uniqueCompanies);
          }
        } catch (firebaseError) {
          console.error("Firebase access error:", firebaseError);
          console.log('Using predefined companies and patterns due to Firebase access error');
          // Continue with default and sample patterns
        }
        
        // Set the companies and patterns in state
        setCompanies(uniqueCompanies);
        setPatterns(companyPatterns);
        
        // Set default selections for first load if not already set
        if (!selectedCompany) setSelectedCompany('Default');
        if (!selectedPattern) setSelectedPattern('Mon-Fri 9-5');
        if (!selectedShift) setSelectedShift('Day');
        
        // Make sure shifts includes the default pattern
        if (!shifts.some(s => s.id === 'default-pattern')) {
          setShifts([defaultPattern]);
        }
        
        console.log('Default Mon-Fri 9-5 pattern available');
      } catch (error) {
        console.error("Error in fetchCompanies:", error);
      }
    };
    
    // Helper to fetch user shift preferences from Firestore
    const fetchUserPreferences = async () => {
      if (!currentUser) return null;
      try {
        // Query the users collection for the current user's document by email
        const userRef = collection(db, 'users');
        const q = query(userRef, where('email', '==', currentUser.email));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          if (userData.shiftPreferences) {
            // Return the preferences object
            return userData.shiftPreferences;
          }
        }
      } catch (error) {
        console.error('Error fetching user shift preferences:', error);
      }
      return null;
    };

    // Main function to fetch companies and set defaults or user prefs
    const initCalendar = async () => {
      await fetchCompanies(); // Always fetch companies and patterns
      // If user is logged in, try to get their preferences
      if (currentUser) {
        const prefs = await fetchUserPreferences();
        if (prefs) {
          // Set selections from user preferences if available
          setSelectedCompany(prefs.company || 'Default');
          setUserInitialPreferredCompany(prefs.company || 'Default'); // Store initial
          setSelectedPattern(prefs.pattern || 'Mon-Fri 9-5');
          setUserInitialPreferredPattern(prefs.pattern || 'Mon-Fri 9-5'); // Store initial
          setSelectedShift(prefs.shift || 'Day');
          setUserInitialPreferredShift(prefs.shift || 'Day'); // Store initial
          // console.log('User Prefs in initCalendar:', prefs); // Debug log removed
          // console.log('Set Initial Preferred: Comp:', prefs.company || 'Default', 'Patt:', prefs.pattern || 'Mon-Fri 9-5', 'Shift:', prefs.shift || 'Day'); // Debug log removed
          return; // Do not override with fallback below
        }
      }
      // If not logged in or no preferences, set defaults if not already set
      if (!selectedCompany) setSelectedCompany('Default');
      if (!selectedPattern) setSelectedPattern('Mon-Fri 9-5');
      if (!selectedShift) setSelectedShift('Day');
    };

    initCalendar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]); // Run when currentUser changes

  // Refresh company logo whenever the selected company changes
  useEffect(() => {
    if (selectedCompany) {
      // Always fetch the latest logo from the database to ensure we have the most up-to-date version
      fetchCompanyLogo(selectedCompany);
      
      // Default company data if not found
      if (!companiesData[selectedCompany]) {
        setCompaniesData(prev => ({
          ...prev,
          [selectedCompany]: { logoUrl: '', country: 'UK' }
        }));
      }
    }
  }, [selectedCompany, companiesData]);

  // Fetch shifts when company and pattern are selected
  useEffect(() => {
    // Skip this effect if we don't have both company and pattern selected
    if (!selectedCompany || !selectedPattern) return;
    
    // For the default pattern, we don't need to fetch from Firebase
    if (selectedCompany === 'Default' && selectedPattern === 'Mon-Fri 9-5') {
      // Only update if we don't already have the default pattern in shifts
      const hasDefaultPattern = shifts.some(s => s.id === 'default-pattern');
      if (!hasDefaultPattern) {
        setShifts([defaultPattern]);
      }
      return;
    }
    
    // For non-logged users, we'll still attempt to fetch real patterns from Firebase
    // No custom pattern generation - we want real data
    
    // For other patterns, check if we already have the correct one from Firebase
    const hasCorrectPattern = shifts.some(s => 
      s.company === selectedCompany && s.patternName === selectedPattern
    );
    
    // Skip fetching if we already have the correct pattern
    if (hasCorrectPattern) return;
    
    // Function to fetch shifts from Firebase
    const fetchShifts = async () => {
      try {
        console.log(`Fetching shifts for ${selectedCompany} - ${selectedPattern}`);
        
        const shiftsRef = collection(db, 'shiftPatterns');
        const q = query(
          shiftsRef, 
          where('company', '==', selectedCompany),
          where('patternName', '==', selectedPattern)
        );
        
        const querySnapshot = await getDocs(q);
        const shiftsData = [];
        
        querySnapshot.forEach((doc) => {
          shiftsData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        if (shiftsData.length > 0) {
          console.log(`Found ${shiftsData.length} shifts for ${selectedCompany}`);
          setShifts(shiftsData);
        } else {
          console.log('No shifts found for selected company and pattern, using default');
          // If no shifts found, fall back to default pattern
          setShifts([defaultPattern]);
        }
      } catch (error) {
        console.error("Error fetching shifts:", error);
        // On error, use default pattern
        setShifts([defaultPattern]);
      }
    };
    
    fetchShifts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, selectedPattern, currentUser]); // Include currentUser in dependencies

  // Fetch all shift patterns for all friends (and user) when friends are loaded
  useEffect(() => {
    const fetchAllFriendsShifts = async () => {
      if (friends.length === 0) return;
      try {
        const shiftsRef = collection(db, 'shiftPatterns');
        // Fetch all shift patterns for all friends (and user)
        const q = query(shiftsRef);
        const querySnapshot = await getDocs(q);
        const allShifts = [];
        querySnapshot.forEach((doc) => {
          allShifts.push({ id: doc.id, ...doc.data() });
        });
        setAllFriendsShifts(allShifts);
      } catch (error) {
        console.error("Error fetching all friends' shifts:", error);
      }
    };
    fetchAllFriendsShifts();
  }, [friends]);

  // Fetch friends when user is logged in
  useEffect(() => {
    const fetchFriends = async () => {
      if (currentUser) {
        try {
          const friendsRef = collection(db, 'users', currentUser.uid, 'friends');
          const querySnapshot = await getDocs(friendsRef);
          const friendsData = [];
          
          querySnapshot.forEach((doc) => {
            friendsData.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          setFriends(friendsData);
        } catch (error) {
          console.error("Error fetching friends:", error);
        }
      }
    };
    
    fetchFriends();
  }, [currentUser]);
  
  // Fetch user's booked holidays from Firestore
  const fetchUserHolidays = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const holidaysRef = collection(db, 'holidays');
      const q = query(holidaysRef, where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      const holidays = [];
      querySnapshot.forEach((doc) => {
        const holidayData = doc.data();
        // Handle different date formats from Firestore
        let startDate, endDate;
        
        // Check if startDate is a Firestore timestamp
        if (holidayData.startDate && typeof holidayData.startDate.toDate === 'function') {
          startDate = holidayData.startDate.toDate();
        } 
        // Check if it's a Date object
        else if (holidayData.startDate instanceof Date) {
          startDate = holidayData.startDate;
        } 
        // Check if it's a string (ISO format)
        else if (typeof holidayData.startDate === 'string') {
          startDate = new Date(holidayData.startDate);
        }
        
        // Same checks for endDate
        if (holidayData.endDate && typeof holidayData.endDate.toDate === 'function') {
          endDate = holidayData.endDate.toDate();
        } 
        else if (holidayData.endDate instanceof Date) {
          endDate = holidayData.endDate;
        } 
        else if (typeof holidayData.endDate === 'string') {
          endDate = new Date(holidayData.endDate);
        }
        
        // Only add valid holiday entries
        if (startDate && endDate) {
          holidays.push({
            id: doc.id,
            startDate,
            endDate,
            status: holidayData.status
          });
        }
      });
      
      setUserHolidays(holidays);
      console.log('Fetched user holidays:', holidays);
    } catch (error) {
      console.error('Error fetching user holidays:', error);
    }
  }, [currentUser]);

  // Effect to fetch holidays when user changes
  useEffect(() => {
    if (currentUser) {
      fetchUserHolidays();
    }
  }, [currentUser, fetchUserHolidays]);

  // Effect to fetch holidays when user changes or when month/year changes
  useEffect(() => {
    if (currentUser) {
      console.log('Fetching holidays for user:', currentUser.uid, 'Month:', currentMonth + 1, 'Year:', currentYear);
      fetchUserHolidays();
    }
  }, [currentMonth, currentYear, fetchUserHolidays, currentUser]);
  
  // Effect to log holidays when they change (for debugging)
  useEffect(() => {
    console.log('User holidays updated:', userHolidays);
  }, [userHolidays]);

  // Handle shift selection when pattern changes
  useEffect(() => {
    if (selectedPattern && !selectedShift) {
      // Set a default shift when pattern is selected but no shift is selected
      setSelectedShift('Day');
    }
  }, [selectedPattern, selectedShift]);
  
  // Handle user login/logout status changes
  useEffect(() => {
    // This will run when currentUser changes (login/logout)
    console.log(currentUser ? 'User logged in' : 'No user logged in');
  }, [currentUser]);
  
  // Generate calendar days for the current month and year
  const generateCalendarDays = useCallback(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    
    const daysInMonth = lastDayOfMonth.getDate();
    const firstDayOfWeek = firstDayOfMonth.getDay() || 7; // Convert Sunday (0) to 7 for European calendar
    
    const days = [];
    
    // Add days from previous month to fill the first week
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = firstDayOfWeek - 1; i > 0; i--) {
      const day = prevMonthLastDay - i + 1;
      const month = currentMonth - 1 < 0 ? 11 : currentMonth - 1;
      const year = currentMonth - 1 < 0 ? currentYear - 1 : currentYear;
      days.push({
        day,
        month,
        year,
        isCurrentMonth: false,
        isWeekend: false, // Will be set correctly below
        date: new Date(year, month, day)
      });
    }
    
    // Add days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayOfWeek = date.getDay();
      days.push({
        day,
        month: currentMonth,
        year: currentYear,
        isCurrentMonth: true,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6, // Sunday or Saturday
        date
      });
    }
    
    // Add days from next month to complete the last week
    const lastDayOfWeek = lastDayOfMonth.getDay() || 7; // Convert Sunday (0) to 7 for European calendar
    const daysToAdd = 7 - lastDayOfWeek;
    
    for (let i = 1; i <= daysToAdd; i++) {
      const day = i;
      const month = currentMonth + 1 > 11 ? 0 : currentMonth + 1;
      const year = currentMonth + 1 > 11 ? currentYear + 1 : currentYear;
      days.push({
        day,
        month,
        year,
        isCurrentMonth: false,
        isWeekend: false, // Will be set correctly below
        date: new Date(year, month, day)
      });
    }
    
    // Set weekend flag for all days
    days.forEach(day => {
      const dayOfWeek = day.date.getDay();
      day.isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
    });
    
    setCalendarDays(days);
  }, [currentMonth, currentYear]);

  // Effect to update calendar days when month or year changes
  useEffect(() => {
    generateCalendarDays();
  }, [currentMonth, currentYear, generateCalendarDays]);
  
  // Navigate to previous month
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Navigate to next month
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  // Handle touch start event
  const handleTouchStart = (e) => {
    e.preventDefault(); // Prevent default touch behavior
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.touches[0].clientX);
  };
  
  // Handle touch move event
  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling while swiping
    if (!touchStart) return;
    setTouchEnd(e.touches[0].clientX);
  };
  
  // Handle touch end event to determine swipe direction
  const handleTouchEnd = (e) => {
    e.preventDefault(); // Prevent default behavior
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 30; // Reduced threshold for mobile
    const isRightSwipe = distance < -30;
    
    // Navigate based on swipe direction
    if (isLeftSwipe) {
      console.log('Swiping left - going to next month');
      nextMonth(); // Swipe left to go to next month
    } else if (isRightSwipe) {
      console.log('Swiping right - going to previous month');
      prevMonth(); // Swipe right to go to previous month
    }
    
    // Reset touch coordinates
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Handle touch cancel event
  const handleTouchCancel = (e) => {
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Mouse events for desktop testing
  const handleMouseDown = (e) => {
    setTouchStart(e.clientX);
  };
  
  const handleMouseMove = (e) => {
    if (!touchStart) return;
    setTouchEnd(e.clientX);
  };
  
  const handleMouseUp = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      console.log('Mouse swipe left - going to next month');
      nextMonth();
    } else if (isRightSwipe) {
      console.log('Mouse swipe right - going to previous month');
      prevMonth();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Handle mouse leave to prevent stuck state
  const handleMouseLeave = () => {
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Fetch company logo from the database
  const fetchCompanyLogo = async (companyName) => {
    try {
      console.log('Fetching logo for company:', companyName);
      
      // Always fetch directly from the database to ensure we have the latest logo
      const companiesRef = collection(db, 'companies');
      const q = query(companiesRef, where('name', '==', companyName));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const companyData = querySnapshot.docs[0].data();
        console.log('Company data found:', companyData);
        
        if (companyData.logoUrl) {
          console.log('Found logo URL:', companyData.logoUrl.substring(0, 50) + '...');
          setCompanyLogo(companyData.logoUrl);
        } else {
          console.log('No logo found for company:', companyName);
          setCompanyLogo(''); // Reset to default if no logo
        }
      } else {
        console.log('Company not found in companies collection:', companyName);
        setCompanyLogo(''); // Reset to default if company not found
      }
    } catch (error) {
      console.error('Error fetching company logo:', error);
      setCompanyLogo(''); // Reset to default on error
    }
  };

  // Handle company change
  const handleCompanyChange = (e) => {
    const company = e.target.value;
    setSelectedCompany(company);
    
    // Reset pattern and shift when company changes
    if (patterns[company] && patterns[company].length > 0) {
      setSelectedPattern(patterns[company][0]);
    } else {
      setSelectedPattern('');
    }
    setSelectedShift('');
    
    // Fetch the company logo when company changes
    fetchCompanyLogo(company);
  };

  // Handle pattern change
  const handlePatternChange = (e) => {
    setSelectedPattern(e.target.value);
    setSelectedShift('');
  };

  // Handle shift change
  const handleShiftChange = (e) => {
    setSelectedShift(e.target.value);
    // Options panel was removed, no need to explicitly close it
  };
  
  // Handle friend selection
  const handleFriendChange = (e) => {
    const friendId = e.target.value;
    if (friendId === '') {
      setSelectedFriend(null);
      // Reset to the user's initial preferred settings when viewing their own calendar
      setSelectedCompany(userInitialPreferredCompany);
      setSelectedPattern(userInitialPreferredPattern);
      setSelectedShift(userInitialPreferredShift);
      // Fetch logo for user's preferred company
      if (userInitialPreferredCompany) {
        fetchCompanyLogo(userInitialPreferredCompany);
      }
    } else {
      const friend = friends.find(f => f.id === friendId);
      setSelectedFriend(friend);
      
      if (friend) {
        setSelectedCompany(friend.company);
        setSelectedPattern(friend.pattern);
        setSelectedShift(friend.shift);
      }
    }
    // Options panel was removed, no need to explicitly close it
  };
  
  // Handle date click to show shift details for that date
  const handleDateClick = (day) => {
    // Only allow clicking on days in the current month
    if (!day.isCurrentMonth) return;
    
    const selectedDate = new Date(day.year, day.month, day.day);
    setSelectedDate(selectedDate);
    
    // Create day objects for the day before and day after
    // Use a more reliable method that avoids DST issues
    const dateBefore = new Date(day.year, day.month, day.day);
    dateBefore.setDate(dateBefore.getDate() - 1);
    const dayBefore = {
      day: dateBefore.getDate(),
      month: dateBefore.getMonth(),
      year: dateBefore.getFullYear(),
      isCurrentMonth: true
    };
    
    const dateAfter = new Date(day.year, day.month, day.day);
    dateAfter.setDate(dateAfter.getDate() + 1);
    const dayAfter = {
      day: dateAfter.getDate(),
      month: dateAfter.getMonth(),
      year: dateAfter.getFullYear(),
      isCurrentMonth: true
    };
    
    // Get shift information for the selected date and adjacent days
    const allShifts = [];
    
    // Get people (current user and friends)
    const people = [];
    if (currentUser) {
      people.push({
        id: 'current-user',
        nick: currentUser.displayName || currentUser.email || 'You'
      });
    }
    
    // Add friends to the people array
    friends.forEach(friend => {
      people.push(friend);
    });
    
    // For each person, get shifts for the 3 days (before, selected, after)
    people.forEach(person => {
      const personShifts = {
        name: person.nick,
        shifts: {
          before: person.id === 'current-user' ? 
            getShiftType(dayBefore) : 
            getFriendShiftType(dayBefore, person),
          selected: person.id === 'current-user' ? 
            getShiftType(day) : 
            getFriendShiftType(day, person),
          after: person.id === 'current-user' ? 
            getShiftType(dayAfter) : 
            getFriendShiftType(dayAfter, person)
        }
      };
      
      allShifts.push(personShifts);
    });
    
    setSelectedDateShifts(allShifts);
    setShowDateModal(true);
  };
  
  // Get shift type for a friend on a specific day
  const getFriendShiftType = (day, friend) => {
    if (!friend || !friend.company || !friend.pattern || !friend.shift) return null;
    
    // Find the shift pattern for this friend
    // Use allFriendsShifts to ensure we look up each friend's true pattern
    const friendShiftPattern = allFriendsShifts.find(s => 
      s.company === friend.company && 
      s.patternName === friend.pattern && 
      s.shift === friend.shift
    ); // Will return undefined if not found
    
    if (!friendShiftPattern) return null; // No pattern found for this friend
    
    // Get the start date from the shift data
    let patternStartDate;
    try {
      if (friendShiftPattern.startDate) {
        patternStartDate = new Date(friendShiftPattern.startDate);
      } else {
        patternStartDate = new Date(2021, 0, 1); // Fallback
      }
    } catch (error) {
      patternStartDate = new Date(2021, 0, 1); // Fallback
    }
    
    // Calculate the difference in days using UTC to avoid DST issues
    const utcCurrent = Date.UTC(day.year, day.month, day.day);
    const utcStart = Date.UTC(patternStartDate.getFullYear(), patternStartDate.getMonth(), patternStartDate.getDate());
    const diffTime = utcCurrent - utcStart;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Use the pattern array from the CSV data
    const patternLength = friendShiftPattern.shiftPattern ? parseInt(friendShiftPattern.shiftPattern) : 28;
    
    // Handle dates before the pattern start date correctly
    let patternDay;
    if (diffDays < 0) {
      patternDay = (patternLength - (Math.abs(diffDays) % patternLength)) % patternLength;
    } else {
      patternDay = diffDays % patternLength;
    }
    
    // Get the pattern value for this day
    let patternValue;
    if (Array.isArray(friendShiftPattern.pattern) && friendShiftPattern.pattern.length > patternDay) {
      patternValue = friendShiftPattern.pattern[patternDay];
    } else if (friendShiftPattern[String(patternDay + 1)]) {
      patternValue = friendShiftPattern[String(patternDay + 1)];
    } else {
      patternValue = 'X';
    }
    
    return patternValue;
  };
  
  // Determine shift type for a specific day
  const getShiftType = (day) => {
    if (!selectedShift || !shifts.length) return null;
    
    const shift = shifts.find(s => s.shift === selectedShift);
    if (!shift) return null;
    
    // Special handling for the default Mon-Fri 9-5 pattern
    if (shift.id === 'default-pattern') {
      // Create a proper Date object for the day
      const date = new Date(day.year, day.month, day.day);
      
      // Get the day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
      const dayOfWeek = date.getDay();
      
      // Monday is 1, Friday is 5
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        return 'D'; // Weekday (Mon-Fri) - Day shift
      } else {
        return 'X'; // Weekend (Sat-Sun) - Off
      }
    }
    
    // Regular pattern handling for other patterns
    // Get the start date from the shift data
    let patternStartDate;
    try {
      // Try to parse the startDate from the CSV data
      if (shift.startDate) {
        patternStartDate = new Date(shift.startDate);
      } else {
        // Fallback to January 1, 2021 as default (from CSV data)
        patternStartDate = new Date(2021, 0, 1);
      }
    } catch (error) {
      console.error('Error parsing start date:', error);
      patternStartDate = new Date(2021, 0, 1); // Fallback
    }
    
    // Calculate the difference in days, preserving the sign (negative for dates before start date)
    // Normalize both dates to UTC midnight to avoid DST issues and ensure accurate day counting
    const utcCurrent = Date.UTC(day.year, day.month, day.day);
    const utcStart = Date.UTC(patternStartDate.getFullYear(), patternStartDate.getMonth(), patternStartDate.getDate());
    const diffTime = utcCurrent - utcStart;
    // Use floor to ensure consistent day calculation
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Use the pattern array from the CSV data
    const patternLength = shift.shiftPattern ? parseInt(shift.shiftPattern) : 28;
    
    // Handle dates before the pattern start date correctly
    let patternDay;
    if (diffDays < 0) {
      // For dates before the start date, we need to go backwards in the pattern
      // We add patternLength and use modulo to get the correct day in the cycle
      patternDay = (patternLength - (Math.abs(diffDays) % patternLength)) % patternLength;
    } else {
      // For dates on or after the start date, calculation remains the same
      patternDay = diffDays % patternLength;
    }
    
    // Get the pattern value for this day
    // In CSV data, pattern values are stored in numbered fields (1-28)
    // or in the 'pattern' array
    let patternValue;
    if (Array.isArray(shift.pattern) && shift.pattern.length > patternDay) {
      // Handle pattern items that might be objects with day and value properties
      const patternItem = shift.pattern[patternDay];
      if (typeof patternItem === 'object' && patternItem !== null) {
        patternValue = patternItem.value || 'X';
      } else {
        patternValue = patternItem;
      }
    } else if (shift[String(patternDay + 1)]) {
      // Try to access by index (1-based as in CSV)
      patternValue = shift[String(patternDay + 1)];
    } else {
      // Default to 'X' if no pattern found
      patternValue = 'X';
    }
    
    return patternValue;
  };

  // Background logo styling is applied inline in the img tag

  return (
    <div className="container mt-2 position-relative">
      {/* Background logo */}
      {companyLogo && (
        <img 
          src={companyLogo} 
          alt="Background Logo" 
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            height: '80vh',
            left: '10vw', // move right by 50vw
            opacity: 0.3, // 30% opacity as requested
            pointerEvents: 'none',
            zIndex: 0
          }}
          className="background-logo"
        />
      )}
      <Card style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
        <Card.Header>
          <div className="d-flex align-items-center">
            {/* First column - Logo or placeholder with width equal to header height */}
            <div className="calendar-header-first-column" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {companyLogo ? (
                <img 
                  src={companyLogo} 
                  alt={selectedCompany || "Company Logo"} 
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                  className="img-fluid"
                  onLoad={() => console.log('Company logo loaded successfully')}
                  onError={(e) => {
                    // Fall back to default logo if company logo fails to load
                    console.error('Error loading company logo, falling back to default');
                    console.log('Logo URL that failed:', companyLogo.substring(0, 50) + '...');
                    // Don't change the src, just hide this image and show the default
                    e.target.style.display = 'none';
                    document.getElementById('default-logo').style.display = 'block';
                  }}
                />
              ) : (
                <img 
                  id="default-logo"
                  src={logoImage} 
                  alt="OFF PLANNER" 
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                  className="img-fluid"
                />
              )}
            </div>
            
            {/* Second column - Company and Pattern in two rows */}
            <div className="flex-grow-1 d-flex flex-column justify-content-center">
              {selectedCompany ? (
                <>
                  {/* Company name - upper row */}
                  <div className="calendar-header-company">{selectedCompany}</div>
                  {/* Pattern name - lower row */}
                  <div className="calendar-header-pattern text-muted">{selectedPattern}</div>
                </>
              ) : (
                <div className="calendar-header-company fs-5">SELECT COMPANY AND PATTERN</div>
              )}
            </div>
            
            {/* Third column - Shift with width equal to header height */}
            <div className="calendar-header-third-column" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {selectedShift ? (
                <div className="calendar-header-shift">{selectedShift}</div>
              ) : (
                <div className="calendar-header-pattern text-muted">NO SHIFT</div>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Two-column layout for desktop */}
          <Row>
            {/* Left column - Calendar */}
            <Col lg={8}>
              <div 
                className="d-flex justify-content-between align-items-center mb-3 calendar-header-nav"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchCancel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                style={{ 
                  cursor: 'grab',
                  userSelect: 'none',
                  position: 'relative',
                  touchAction: 'none' // Prevent browser handling of touch gestures
                }}
              >
                <Button variant="outline-primary" className="nav-arrow-btn d-none d-md-block" onClick={prevMonth}>&lt;</Button>
                <h3 className="month-year-display">{monthNames[currentMonth].toUpperCase()} {currentYear}</h3>
                <Button variant="outline-primary" className="nav-arrow-btn d-none d-md-block" onClick={nextMonth}>&gt;</Button>
                <div className="swipe-hint text-muted">
                  <small className="d-block d-md-none">← Swipe to navigate →</small>
                </div>
              </div>
              
              <p className="text-muted mb-2"><small>Click on a day to see shift details</small></p>
              
              <div className="calendar">
                <div className="calendar-header">
                  {dayNames.map((day, index) => (
                    <div key={index} className="calendar-cell header">
                      {day}
                    </div>
                  ))}
                </div>
                
                <div className="calendar-body">
                  {calendarDays.map((day, index) => {
                    const shiftType = getShiftType(day);
                    let cellClass = "calendar-cell";
                    
                    if (!day.isCurrentMonth) {
                      cellClass += " other-month";
                    }
                    
                    if (day.isWeekend) {
                      cellClass += " weekend";
                    }
                    
                    // Check if day is a bank holiday
                    if (isBankHoliday(day)) {
                      cellClass += " bank-holiday";
                    }
                    
                    // Check if day is within a booked holiday period AND viewing own preferred shift
                    const isViewingOwnPreferredShift =
                      !selectedFriend && // Not viewing a friend's calendar
                      selectedCompany === userInitialPreferredCompany &&
                      selectedPattern === userInitialPreferredPattern &&
                      selectedShift === userInitialPreferredShift;

                    if (isHolidayBooked(day) && isViewingOwnPreferredShift) {
                      cellClass += " booked-holiday";
                      // cellClass += " holiday-booked"; // Keeping only one class for clarity now
                    }
                    
                    // Current day
                    if (day.date.setHours(0,0,0,0) === new Date().setHours(0,0,0,0)) {
                      cellClass += " today";
                    }
                    
                    // Selected day
                    if (selectedDate && 
                        day.day === selectedDate.getDate() && 
                        day.month === selectedDate.getMonth() && 
                        day.year === selectedDate.getFullYear()) {
                      cellClass += " selected";
                    }
                    
                    // Apply shift type styling
                    if (shiftType === 'D') {
                      cellClass += " day-shift";
                    } else if (shiftType === 'N') {
                      cellClass += " night-shift";
                    }
                    
                    return (
                      <div 
                        key={index} 
                        className={cellClass}
                        onClick={() => handleDateClick(day)}
                        style={{
                          cursor: 'pointer',
                          position: 'relative'
                        }}
                      >
                        <span className="day-number">{day.day}</span>
                        {/* Only render bank holiday marker if it's actually a bank holiday */}
                        {isBankHoliday(day) && (
                          <div 
                            className="bank-holiday-marker" 
                            style={{
                              position: 'absolute',
                              top: '2px',
                              right: '2px',
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#ff4d4d',
                              zIndex: 1
                            }}
                            title="Bank Holiday"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Shift legend - truly inline for mobile */}
                <div className="text-center mt-2 small">
                  <span className="me-1"></span>
                  <span className="d-inline-block me-2">
                    <span className="legend-color day d-inline-block me-1" style={{ width: '10px', height: '10px', verticalAlign: 'middle' }}></span>
                    Day
                  </span>
                  <span className="d-inline-block me-2">
                    <span className="legend-color night d-inline-block me-1" style={{ width: '10px', height: '10px', verticalAlign: 'middle' }}></span>
                    Night
                  </span>
                  <span className="d-inline-block me-2">
                    <span className="legend-color off d-inline-block me-1" style={{ width: '10px', height: '10px', verticalAlign: 'middle' }}></span>
                    Off
                  </span>
                  <span className="d-inline-block me-2">
                    <span className="d-inline-block me-1" style={{ 
                      width: '10px', 
                      height: '10px', 
                      verticalAlign: 'middle',
                      border: '2px solid #28a745',
                      backgroundColor: 'rgba(40, 167, 69, 0.15)',
                      borderRadius: '50%'
                    }}></span>
                    Holiday
                  </span>
                  <span className="d-inline-block">
                    <span className="d-inline-block me-1" style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ff4d4d', verticalAlign: 'middle' }}></span>
                    Bank Holiday
                  </span>
                </div>
              </div>
            </Col>
            

          </Row>
          {/* Add the MobileMenuBar here, inside Card.Body */}
          <MobileMenuBar
            companies={companies}
            patterns={patterns}
            shifts={shifts}
            selectedCompany={selectedCompany}
            selectedPattern={selectedPattern}
            selectedShift={selectedShift}
            handleCompanyChange={handleCompanyChange}
            handlePatternChange={handlePatternChange}
            handleShiftChange={handleShiftChange}
            currentUser={currentUser}
            friends={friends} // Added
            selectedFriend={selectedFriend} // Added
            handleFriendChange={handleFriendChange} // Added
            onHolidayBooked={fetchUserBookedHolidays} // Pass the refresh function
          />
        </Card.Body>
      </Card>
      {/* Date Selection Modal */}
      <Modal show={showDateModal} onHide={() => setShowDateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDate && `Shifts for ${selectedDate.getDate()} ${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Bank Holiday Indicator */}
          {selectedDate && isBankHoliday(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()) && (
            <div className="bank-holiday-alert mb-3 p-2 text-center" style={{
              backgroundColor: '#ffeeee',
              border: '1px solid #ffcccc',
              borderRadius: '4px',
              color: '#d9534f'
            }}>
              <strong>Bank Holiday</strong>
            </div>
          )}
          
          {selectedDateShifts.length > 0 ? (
            <div className="date-shifts">
              {/* Date navigation header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="text-center" style={{ width: '30%' }}>
                  {selectedDate && (() => {
                    const prevDate = new Date(selectedDate);
                    prevDate.setDate(prevDate.getDate() - 1);
                    return (
                      <div className="small text-muted">
                        {prevDate.getDate()} {monthNames[prevDate.getMonth()]}
                      </div>
                    );
                  })()}
                </div>
                <div className="text-center" style={{ width: '40%' }}>
                  <strong>
                    {selectedDate && `${selectedDate.getDate()} ${monthNames[selectedDate.getMonth()]}`}
                  </strong>
                </div>
                <div className="text-center" style={{ width: '30%' }}>
                  {selectedDate && (() => {
                    const nextDate = new Date(selectedDate);
                    nextDate.setDate(nextDate.getDate() + 1);
                    return (
                      <div className="small text-muted">
                        {nextDate.getDate()} {monthNames[nextDate.getMonth()]}
                      </div>
                    );
                  })()}
                </div>
              </div>
              
              {/* Shift information for each person */}
              {selectedDateShifts.map((person, index) => (
                <div key={index} className="shift-item mb-3 p-2" 
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    backgroundColor: '#f8f9fa'
                  }}>
                  <div className="mb-2">
                    <strong>{person.name}</strong>
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center">
                    {/* Day before */}
                    <div className="text-center" style={{ width: '30%' }}>
                      <div 
                        className="shift-circle small" 
                        style={{
                          width: '30px',
                          height: '30px',
                          margin: '0 auto',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: person.shifts.before === 'D' ? '#fff3cd' : 
                                          person.shifts.before === 'N' ? '#e9ecef' : '#ffffff',
                          border: '1px solid #dee2e6',
                          fontSize: '0.8rem'
                        }}
                      >
                        {person.shifts.before || 'X'}
                      </div>
                    </div>
                    
                    {/* Selected day */}
                    <div className="text-center" style={{ width: '40%' }}>
                      <div 
                        className="shift-circle" 
                        style={{
                          width: '40px',
                          height: '40px',
                          margin: '0 auto',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: person.shifts.selected === 'D' ? '#fff3cd' : 
                                          person.shifts.selected === 'N' ? '#e9ecef' : '#ffffff',
                          border: '2px solid #007bff',
                          fontWeight: 'bold'
                        }}
                      >
                        {person.shifts.selected || 'X'}
                      </div>
                      <div className="mt-1 small">
                        {person.shifts.selected === 'D' ? 'Day Shift' : 
                         person.shifts.selected === 'N' ? 'Night Shift' : 'Off'}
                      </div>
                    </div>
                    
                    {/* Day after */}
                    <div className="text-center" style={{ width: '30%' }}>
                      <div 
                        className="shift-circle small" 
                        style={{
                          width: '30px',
                          height: '30px',
                          margin: '0 auto',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: person.shifts.after === 'D' ? '#fff3cd' : 
                                          person.shifts.after === 'N' ? '#e9ecef' : '#ffffff',
                          border: '1px solid #dee2e6',
                          fontSize: '0.8rem'
                        }}
                      >
                        {person.shifts.after || 'X'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No shift information available for this date.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDateModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Calendar;
